import { Box, Button, CircularProgress } from "@material-ui/core";
import { useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { usePoolMonitoringContext } from "src/context/monitoring/poolMonitoringContext";
import { useProviderContext } from "src/ether-utils/context/providerContext";
import { encodePackageData, getContractEditPool } from "src/hooks/useContractEditPool";
import useLowerAddressParam from "src/hooks/useLowerAddressParam";
import useNotify from "src/hooks/useNotify";
import { toFixed } from "src/services/utils";
import { useImmer } from "use-immer";
import StyledTextField from "../components/StyledTextField";
import { onTextChange } from "../components/util";
import { defaultConfig } from "./ReserveParameterDialog";

export default function TabReserveParameter({ asset }) {
  const mounted = useRef(false);
  const { pool } = useLowerAddressParam();
  const { errorNotify, successNotify } = useNotify();
  const { data } = usePoolMonitoringContext();
  const { currentProvider, wallet } = useProviderContext();
  const [tokenValue, setTokenValue] = useImmer({
    ltv: asset.risk.maxLTV,
    lt: asset.risk.liqThres,
    lb: asset.risk.liqBonus,
  });
  const [helperText, setHelperText] = useImmer({
    ltv: null,
    lt: null,
    lb: null,
  });
  const [loading, setLoading] = useState(false);
  const history = useHistory();

  const handleConfirm = async () => {
    setLoading(true);
    const contractEditPool = getContractEditPool(wallet.signer);
    try {
      const dataEncode = await encodePackageData(currentProvider, "configureReserveAsCollateral", [
        asset.address,
        toFixed(Number(tokenValue.ltv) * 10000),
        toFixed(Number(tokenValue.lt) * 10000),
        toFixed(Number(tokenValue.lb + 1) * 10000),
      ]);
      const _promise = await contractEditPool.submitTransaction(data[pool].overview.providerId, dataEncode);
      await _promise.wait();
      successNotify("Transaction Successfully");
      history.push(`/pool-monitoring/${pool}?action=pending`);
      window.location.reload(false);
    } catch (error) {
      console.error(error);
      errorNotify(error?.message);
    } finally {
      setLoading(false);
    }
  };

  function validate(key, value) {
    const _value = value[key];
    const _number = Number(_value);
    if (_value === null || _value == "") {
      setHelperText((draft) => {
        draft[key] = "This field is required";
      });
    } else if (_number > 1 || _number < 0) {
      setHelperText((draft) => {
        draft[key] = "This field must be less than 1 and greater than 0";
      });
    } else
      setHelperText((draft) => {
        draft[key] = null;
      });
  }

  useEffect(() => {
    if (mounted.current) {
      validate("ltv", tokenValue);
      validate("lt", tokenValue);
      validate("lb", tokenValue);
      if (tokenValue.lt < tokenValue.ltv) {
        setHelperText((draft) => {
          draft["ltv"] = "This field must be less than or equal liquidation threshold";
        });
      }
    }
    mounted.current = true;
  }, [tokenValue.ltv, tokenValue.lt, tokenValue.lb]);

  const isInvalid = useMemo(() => {
    return helperText["ltv"] !== null || helperText["lt"] !== null || helperText["lb"] !== null;
  }, [helperText]);

  const isSimilar = useMemo(() => {
    return (
      asset.risk.maxLTV == tokenValue.ltv &&
      asset.risk.liqThres == tokenValue.lt &&
      asset.risk.liqBonus == tokenValue.lb
    );
  }, [asset, tokenValue]);

  return (
    <Box mt={4}>
      <StyledTextField
        textProps={{
          ...defaultConfig,
          label: "Loan to Value",
          value: tokenValue.ltv,
          onChange: (e) => onTextChange("ltv", e.target.value, setTokenValue),
          error: helperText.ltv != null,
          helperText: helperText.ltv,
        }}
        rootProps={{ mb: 2 }}
        info="A financial term used by lenders to express the ratio of a loan to the value of an asset purchased."
      />
      <StyledTextField
        textProps={{
          ...defaultConfig,
          label: "Liquidation Threshold",
          value: tokenValue.lt,
          onChange: (e) => onTextChange("lt", e.target.value, setTokenValue),
          error: helperText.lt != null,
          helperText: helperText.lt,
        }}
        rootProps={{ mb: 2 }}
        info="The percentage at which a position is defined as undercollateralized. For example, a Liquidation threshold of 80% means that if the value rises above 80% of the collateral, the position is undercollateralized and could be liquidated."
      />
      <StyledTextField
        textProps={{
          ...defaultConfig,
          label: "Liquidation Bonus",
          value: tokenValue.lb,
          onChange: (e) => onTextChange("lb", e.target.value, setTokenValue),
          error: helperText.lb != null,
          helperText: helperText.lb,
        }}
        rootProps={{ mb: 2 }}
        info="The reserve factor allocates a share of the protocol's interests to a collector contract as a reserve for the ecosystem."
      />
      <Box>
        <Button
          disabled={isInvalid || loading || isSimilar}
          fullWidth
          variant="contained"
          color="primary"
          onClick={handleConfirm}
        >
          {!loading ? "Confirm" : <CircularProgress size={24} />}
        </Button>
      </Box>
    </Box>
  );
}
